import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { RiSearchLine, RiMicLine, RiCameraLine, RiCloseLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../constants/Url';
import httpClient from '../utils/HttpClient';
import { useSelector } from 'react-redux';
import AuthModal from './auth/AuthModal';
import { useRef } from 'react';
import CustomPagination from './common/CustomPagination';

const GlobalSearchModal = ({ open, onCancel }) => {
  const [searchText, setSearchText] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const user = useSelector((state) => state.authReducer);
  const token = user && user?.token ? user?.token : null;
  const authModalRef = useRef();

  // Tab configurations matching originModel
  const searchTabs = [
    { label: t('All'), value: 'ALL' },
    { label: t('menu_news'), value: 'News' },
    { label: t('category_fashion'), value: 'Fashion' },
    { label: t('menu_tech_edu'), value: 'Technicals' },
    { label: t('text_teacher'), value: 'Teacher' },
    { label: t('text_course'), value: 'Course' },
    { label: t('category_product'), value: 'Product' },
    { label: t('menu_magazine'), value: 'Magazine' }
  ];

  // clear state on close
  useEffect(() => {
    if (!open) {
      setSearchText('');
      setHasSearched(false);
      setActiveTab('ALL');
      setSearchResults([]);
      setCurrentPage(1);
      setTotalResults(0);
    }
  }, [open]);

  const fetchSearchResults = async (keyword, tabValue, page = 1, limit = 10) => {
    if (!keyword.trim()) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const modelParam = tabValue === 'ALL' ? '' : tabValue;
      const res = await httpClient.get(`/searching?keyword=${keyword}&originModel=${modelParam}&pageNo=${page}&pageSize=${limit}`);
      if (res.data) {
        const list = res.data?.data || res.data?.list || res.data?.records || res.data || [];
        setSearchResults(Array.isArray(list) ? list : []);
        setTotalResults(res.data?.total || list.length || 0);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!token) {
      authModalRef.current?.open();
      return;
    }
    setCurrentPage(1);
    fetchSearchResults(searchText, activeTab, 1, pageSize);
  };

  // Re-fetch when tab changes (if already searched)
  useEffect(() => {
    if (hasSearched && searchText.trim()) {
      setCurrentPage(1);
      fetchSearchResults(searchText, activeTab, 1, pageSize);
    }
  }, [activeTab]);

  const handlePageChange = (page, limit) => {
    if (limit !== pageSize) {
      setPageSize(limit);
      setCurrentPage(1);
      fetchSearchResults(searchText, activeTab, 1, limit);
    } else {
      setCurrentPage(page);
      fetchSearchResults(searchText, activeTab, page, limit);
    }
  };

  return (
    <>
    <AuthModal ref={authModalRef} />
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={!hasSearched}
      width="100%"
      style={{ top: 0, margin: 0, padding: 0, maxWidth: '100vw', height: '100vh' }}
      className="google-style-search-modal"
      styles={{
        mask: { backgroundColor: 'white' },
        content: { padding: 0, backgroundColor: 'white', boxShadow: 'none', height: '100vh', borderRadius: 0 },
        body: { padding: 0, height: '100%' }
      }}
    >
      {!hasSearched ? (
        // Homepage Centered View
        <div className="flex flex-col items-center justify-center h-full pb-[15vh]">
          {/* Logo Area */}
          <div className="mb-8 select-none">
            <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} onClick={() => { navigate("/") }} className='esi-logo' />
          </div>

          {/* Search Input Bar */}
          <div className="w-full max-w-[584px] px-4 group">
            <div className="flex items-center px-4 py-3 bg-white border border-gray-200 rounded-full hover:shadow-[0_1px_6px_rgba(32,33,36,0.28)] focus-within:shadow-[0_1px_6px_rgba(32,33,36,0.28)] transition-shadow duration-200">
              <RiSearchLine className="text-gray-400 text-xl mr-3 shrink-0" />
              <input
                autoFocus
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full bg-transparent border-none outline-none text-[16px] text-gray-800"
              />
              <div className="flex items-center gap-3 shrink-0 ml-2">
                {searchText && (
                  <RiCloseLine className="text-xl text-gray-500 cursor-pointer hover:text-gray-700 pr-2" onClick={() => setSearchText('')} />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Search Results Header View
        <div className="w-full flex flex-col pt-4 md:pt-6 border-b border-gray-200">
          <div className="flex items-center px-4 md:px-8 mb-4 md:mb-6">
            {/* Logo */}
            <div className="select-none cursor-pointer mr-4 md:mr-10 hidden sm:block" onClick={() => { setHasSearched(false); setSearchText(''); }}>
              <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} onClick={() => { navigate("/") }} className='esi-logo w-[80px] md:w-auto' />
            </div>

            {/* Search Input Bar (Header version) */}
            <div className="w-full max-w-[690px]">
              <div className="flex items-center px-4 py-2.5 bg-white border border-transparent shadow-[0_2px_5px_rgba(32,33,36,0.15)] rounded-full hover:shadow-[0_2px_8px_rgba(32,33,36,0.2)] focus-within:shadow-[0_2px_8px_rgba(32,33,36,0.2)] transition-shadow duration-200">
                <input
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full bg-transparent border-none outline-none text-[16px] text-gray-800 ml-2"
                />
                <div className="flex items-center gap-3 shrink-0 ml-2">
                  {searchText && (
                    <RiCloseLine className="text-xl md:text-2xl text-gray-500 cursor-pointer hover:text-gray-700 pr-2 border-r border-gray-200" onClick={() => setSearchText('')} />
                  )}
                  {/* <RiMicLine className="text-xl text-[#4285F4] cursor-pointer" />
                  <RiCameraLine className="text-xl text-[#EA4335] cursor-pointer" /> */}
                  <RiSearchLine className="text-xl text-[#4285F4] cursor-pointer ml-1" onClick={handleSearch} />
                </div>
              </div>
            </div>

            <div className="ml-auto pl-2">
              <RiCloseLine className="text-2xl md:text-3xl text-gray-500 cursor-pointer hover:text-gray-800 bg-gray-100 rounded-full p-1 shrink-0" onClick={onCancel} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-4 md:gap-8 px-4 md:px-10 lg:px-40 text-sm font-medium h-12 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {searchTabs.map(tab => (
              <div
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`relative h-full flex items-center cursor-pointer transition-colors whitespace-nowrap shrink-0 ${activeTab === tab.value ? 'text-black' : 'text-gray-500 hover:text-gray-800'}`}
              >
                {tab.label}
                {activeTab === tab.value && (
                  <div className="absolute bottom-1.5 left-0 right-0 h-[3px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] rounded-full" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search results area */}
      {hasSearched && (
        <div className="px-4 md:px-10 lg:px-40 py-4 md:py-8 text-gray-600 max-h-[calc(100vh-140px)] overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-400">{t("text_searching")}</div>
          ) : searchResults.length > 0 ? (
            <>
              {searchResults.map((result, idx) => (
                <div 
                  key={result.id || result._id || idx} 
                  className="mb-8 max-w-[800px] cursor-pointer hover:bg-gray-50 p-4 -ml-4 rounded-xl transition-colors"
                  onClick={() => {
                    const model = result.originModel ? result.originModel : '';
                    let targetUrl = '';
                    // Fashion News Magazine Teacher Course Technicals Product
                    switch (model) {
                      case 'Technicals':
                        targetUrl = `/technical/${result.originId || result.code || result.id}`;
                        break;
                      case 'Teacher':
                        targetUrl = `/online-class/${result.originId || result.id}`;
                        break;
                      case 'Course':
                        targetUrl = `/online-class-member/${result.originId || result.id}`;
                        break;
                      case 'News':
                        targetUrl = `/news/${result.originId || result.code || result.id}`;
                        break;
                      case 'Fashion':
                        targetUrl = `/fashion/${result.originId || result.id || result._id}`;
                        break;
                      case 'Magazine':
                        targetUrl = `/magazine/${result.originId || result.id || result._id}`;
                        break;
                      case 'Product':
                        targetUrl = `/product/${result.originId || result.id || result._id}`;
                        break;
                      default:
                        targetUrl = `/`;
                        console.warn("Unmatched originModel:", result.originModel, result);
                    }
                    
                    if (targetUrl) {
                      navigate(targetUrl);
                      onCancel();
                    }
                  }}
                >
                  <h3 className="text-xl text-[#1a0dab] hover:underline mb-1 line-clamp-1">
                    {result.title || result.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-1">
                    {result.originModel || 'Result'} &bull; {result.createdAt ? new Date(result.createdAt).toLocaleDateString() : ''}
                  </p>
                  <p className="text-sm line-clamp-2">
                    {result.description || result.content || result.intro || result.subTitle || ''}
                  </p>
                </div>
              ))}
              
              {totalResults > 0 && (
                <div className="flex justify-start mt-8 pb-8">
                  <CustomPagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalResults}
                    onChange={handlePageChange}
                    pageSizeOptions={[10, 20, 30, 40, 50]}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="py-10 text-center text-gray-400">
               {t('暂无关于')} "{searchText}" {t('的搜索结果')}
            </div>
          )}
        </div>
      )}
    </Modal>
    </>
  );
};

export default GlobalSearchModal;
